// FILE: relativeTime.ts
// Purpose: The one short "how long ago" label the app renders beside timestamps.
// Layer: Client formatting helper
// Exports: formatRelativeTime
//
// Extracted from `Sidebar.tsx`, which still re-exports it: the thread list, the search palette
// and the Pull Requests section all label the same kind of instant, and a second implementation
// is how "2h" in one list and "2 hours ago" in another start disagreeing.

/**
 * Milliseconds left until an ISO instant, or `null` when there are none.
 *
 * `null` covers the three cases a caller has to treat the same way — no
 * deadline, an unparseable one, and one that has already passed — because every
 * reader of this is a countdown, and a negative number rendered as a countdown
 * is how "expired" starts reading as "expires in -3 h".
 */
export function remainingMsUntil(iso: string | null | undefined, nowMs: number): number | null {
  if (iso === null || iso === undefined) return null;
  const deadline = Date.parse(iso);
  if (!Number.isFinite(deadline)) return null;
  const remaining = deadline - nowMs;
  return remaining > 0 ? remaining : null;
}

/** Compact age of an ISO instant: `now`, `12m`, `5h`, `3d`. Never a future value. */
export function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
